import type { Topic } from '../components/layout/TopicIndex'
import type { SolutionId } from './solutionCatalog'

/** Deeper, solution-specific content shown below the shared solution structure. */
export type SolutionDepth = {
  heading: string
  note?: string
  topics: Topic[]
}

export const solutionDepth: Partial<Record<SolutionId, SolutionDepth>> = {
  education: {
    heading: 'Education in more depth',
    note: 'The current Education demo uses fictional adult learners at the Nordland Adult Learning Studio. Early-childhood and school-age applications are proposed, age-appropriate research directions, not validated current assessments.',
    topics: [
      {
        title: 'Explore the Education workflow',
        aliasIds: ['planned-noetivra-uses-in-education'],
        paragraphs: [
          'A learner reads a task with its criteria, submits a written response and receives criterion-by-criterion feedback from an educator. A completed review leads to a focused practice activity, which the learner completes with a reflection before a reassessment in a changed scenario.',
          'Educators see each submission beside its criteria, assign practice and compare reassessment evidence. The emphasis is on learning and support.',
        ],
      },
      {
        title: 'Early Development and Foundational Readiness',
        paragraphs: [
          'A proposed research direction examines how age-appropriate activities can support emotional regulation, attention, cooperation and basic cause-and-effect reasoning. Work with young children needs methods designed and validated for their age, with caregivers and educators leading.',
        ],
      },
      {
        title: 'Primary and Secondary Education',
        paragraphs: [
          'A proposed school-age application helps schools strengthen reasoning, self-direction, media literacy, planning and consequential thinking alongside academic learning. These are learning supports first; any assessment needs validation for school-age learners and its intended use.',
        ],
      },
      {
        title: 'Media Literacy and Manipulation Resistance',
        paragraphs: [
          'Learners face information overload, emotional framing and persuasive media. Activities help them practise recognizing misleading framing and selective evidence while staying open to well-supported information.',
        ],
      },
      {
        title: 'Higher Education and Readiness for Responsibility',
        paragraphs: [
          'In universities and adult learning, the workflow supports critical thinking, long-range planning and preparation for professional and civic responsibility.',
        ],
      },
      {
        title: 'Support for Educators and Institutions',
        paragraphs: [
          'Institutions can review participation, support needs and progress across programs to inform curriculum design and student support. Program-level patterns are not treated as judgments about individual learners.',
        ],
      },
    ],
  },
  talent: {
    heading: 'Workforce in more depth',
    topics: [
      {
        title: 'Structured Interviews and Candidate Review',
        paragraphs: [
          'Each role requirement connects to a task, and reviewers record observations, explanations and unanswered questions against shared criteria. Comparison views show the same criteria for each candidate with links to every record.',
          'A separate decision form—request follow-up, advance or do not advance—requires reviewed evidence and a written reason. Hiring decisions are recorded by people, and hiring assessments are validated for their intended use, with accessibility options and checks for unequal outcomes.',
        ],
      },
      {
        title: 'Workforce Development and Role Readiness',
        paragraphs: [
          'Employers can understand capabilities that shape role readiness, such as planning, communication and consequential thinking, and connect them to development.',
        ],
      },
      {
        title: 'Advancement and Internal Mobility',
        paragraphs: [
          'Development pathways help employees prepare for greater responsibility. Opportunity guidance stays separate from eligibility decisions, which remain with authorized people.',
        ],
      },
      {
        title: 'Organizational Resilience',
        paragraphs: [
          'Aggregate development data supports succession planning and capability-building. Group patterns do not establish any individual’s capabilities, and outcomes also depend on resources, incentives and working conditions.',
        ],
      },
    ],
  },
  community: {
    heading: 'Community work in more depth',
    topics: [
      {
        title: 'Consent-Based Partner Referrals',
        paragraphs: [
          'A program team records the participant, receiving organization, purpose and exact information to share, with confirmation that the participant agreed. Each status change requires a reason and appears in the participant’s view.',
          'Community organizations participate in shared programs without receiving unrestricted access to government or partner records. A referral never grants access to a participant’s complete record.',
        ],
      },
      {
        title: 'Community Knowledge and Collaborative Program Review',
        paragraphs: [
          'Organizations often hold valuable knowledge in disconnected reports and evaluations. Connected knowledge helps teams find what has been tried and what was learned, and review programs together while each organization controls what it shares.',
        ],
      },
      {
        title: 'Mentorship and Upward Mobility',
        paragraphs: [
          'Development plans connect a participant’s goals and barriers with mentoring, training and community support, with the participant agreeing each step.',
        ],
      },
      {
        title: 'Support Systems and Opportunity Alignment',
        paragraphs: [
          'Aggregate views show where support is missing, such as reported barriers to participation and access to support. Group patterns describe programs and communities, not individuals.',
        ],
      },
    ],
  },
  leadership: {
    heading: 'Leadership in more depth',
    topics: [
      {
        title: 'Scenario Exercises, Decision Reviews, and Progress Views',
        paragraphs: [
          'Leaders work through scenario exercises and crisis simulations, then review their decisions alongside the reasoning, trade-offs and consequences involved. Progress views show change across reassessments without an overall score.',
        ],
      },
      {
        title: 'One Leadership Capability, Many Programs',
        paragraphs: [
          'The same exercises run inside government, education and workforce programs, so organizations can share scenarios and coaching approaches rather than rebuilding them for each setting.',
        ],
      },
      {
        title: 'Leadership Pipelines and Succession',
        paragraphs: [
          'Organizations can use development records to plan succession and advancement. Pilots examine how exercise results relate to later performance.',
        ],
      },
      {
        title: 'Executive and High-Responsibility Environments',
        paragraphs: [
          'In senior roles, exercises focus on consequence reasoning, crisis stability and communication under pressure. Role qualification is a separate use that requires evidence for the particular role and human review.',
        ],
      },
    ],
  },
  government: {
    heading: 'Government in more depth',
    topics: [
      {
        title: 'Policy Exploration, Staff Learning, and Authorized Review',
        paragraphs: [
          'Approved policies, program knowledge and rules are organized with their sources and versions, so staff can find current guidance. Staff follow learning activities tied to their roles and take part in authorized review workflows.',
        ],
      },
      {
        title: 'Institutional Performance',
        paragraphs: [
          'Service delays, access barriers, administrative errors and unresolved requests are visible alongside program outcomes, so institutions improve in the same cycle as the people they serve.',
        ],
      },
      {
        title: 'Public Workforce and Leadership Readiness',
        paragraphs: [
          'Public-sector training and succession planning use the shared Capability module and leadership exercises. Role qualification is a separate use that requires evidence for the particular role and human review.',
        ],
      },
    ],
  },
}

/** NCDS proposed public-service and program improvement cycle. */
export const ncdsCycle = [
  'Identify needs',
  'Establish a baseline',
  'Agree a support plan',
  'Deliver support',
  'Measure outcomes',
  'Improve delivery',
]

export const governmentScales: { title: string; description: string; href?: string }[] = [
  { title: 'Municipal & Local', description: 'Community support patterns and local delivery', href: '/municipal-scale' },
  { title: 'Regional & Provincial', description: 'Broader planning across education, labor and resilience' },
  { title: 'National Government', description: 'Ministry-level planning and civil-service modernization', href: '/national-scale' },
]

export const governmentScalesNote =
  'These groupings describe where the solution can be applied. The fictional Nordland demonstration uses a more detailed navigation hierarchy: National → Region → State → Province → City → District. A real implementation follows the participating country’s administrative structure.'
